import { useState, useCallback } from 'react';

/**
 * Custom hook for JetSetter messages management
 * @returns {Object} Messages state and methods
 */
const useMessages = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mock messages data
  const mockMessages = [
    {
      id: 'MSG001',
      subject: 'Flight Confirmation - JS101',
      content: 'Your flight JS101 from JFK to LAX has been confirmed for March 15, 2024.',
      sender: 'JetScanner Support',
      timestamp: '2024-02-01T10:30:00Z',
      isRead: false,
      type: 'confirmation'
    },
    {
      id: 'MSG002',
      subject: 'Check-in Reminder',
      content: 'Don\'t forget to check in for your upcoming flight JS101. Check-in opens 24 hours before departure.',
      sender: 'JetScanner Support',
      timestamp: '2024-02-14T08:00:00Z',
      isRead: true,
      type: 'reminder'
    },
    {
      id: 'MSG003',
      subject: 'Special Offer - 20% Off',
      content: 'Book your next flight with us and get 20% off. Use code SAVE20 at checkout.',
      sender: 'JetScanner Marketing',
      timestamp: '2024-02-10T12:00:00Z',
      isRead: false,
      type: 'promotion'
    }
  ];

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setMessages(mockMessages);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const getMessage = useCallback(async (messageId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const message = mockMessages.find(m => m.id === messageId);
      if (!message) {
        throw new Error('Message not found');
      }
      
      setSelectedMessage(message);
      return message;
    } catch (err) {
      console.error('Failed to get message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (messageId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, isRead: true } : msg
      ));
    } catch (err) {
      console.error('Failed to mark message as read:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setMessages(prev => prev.map(msg => ({ ...msg, isRead: true })));
    } catch (err) {
      console.error('Failed to mark all messages as read:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteMessage = useCallback(async (messageId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (err) {
      console.error('Failed to delete message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSelectedMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setMessages(prev => prev.filter(msg => !msg.isSelected));
    } catch (err) {
      console.error('Failed to delete selected messages:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMessage = useCallback(async (messageData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newMessage = {
        id: `MSG${Date.now()}`,
        subject: messageData.subject,
        content: messageData.content,
        sender: 'You',
        timestamp: new Date().toISOString(),
        isRead: true,
        type: 'sent'
      };
      
      setMessages(prev => [newMessage, ...prev]);
      return newMessage;
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleMessageSelection = useCallback((messageId) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, isSelected: !msg.isSelected } : msg
    ));
  }, []);

  const getUnreadCount = useCallback(() => {
    return messages.filter(msg => !msg.isRead).length;
  }, [messages]);

  return {
    messages,
    selectedMessage,
    loading,
    error,
    loadMessages,
    getMessage,
    markAsRead,
    markAllAsRead,
    deleteMessage,
    deleteSelectedMessages,
    sendMessage,
    toggleMessageSelection,
    getUnreadCount,
    setSelectedMessage
  };
};

export default useMessages;